/**
 * Utility functions for working with element paths in nested structures
 * Paths are arrays of element IDs representing the hierarchy
 * Example: ['section-1', 'row-2', 'column-3'] means column-3 inside row-2 inside section-1
 */

/**
 * Convert a path array to a droppable ID string
 * Used by react-beautiful-dnd to identify drop zones
 */
export const pathToDroppableId = (path) => {
  if (!path || path.length === 0) {
    return 'root';
  }
  return path.join('__');
};

/**
 * Convert a droppable ID string back to a path array
 */
export const droppableIdToPath = (droppableId) => {
  if (droppableId === 'root') {
    return [];
  }
  return droppableId.split('__');
};

/**
 * Get the parent path from a path
 * Example: ['section-1', 'row-2', 'column-3'] => ['section-1', 'row-2']
 */
export const getParentPath = (path) => {
  if (!path || path.length === 0) {
    return null;
  }
  return path.slice(0, -1);
};

/**
 * Get the last ID from a path (the current element)
 */
export const getLastId = (path) => {
  if (!path || path.length === 0) {
    return null;
  }
  return path[path.length - 1];
};

/**
 * Check if one path is an ancestor of another
 * Example: ['section-1'] is ancestor of ['section-1', 'row-2']
 */
export const isAncestor = (ancestorPath, descendantPath) => {
  if (ancestorPath.length >= descendantPath.length) {
    return false;
  }

  for (let i = 0; i < ancestorPath.length; i++) {
    if (ancestorPath[i] !== descendantPath[i]) {
      return false;
    }
  }

  return true;
};

/**
 * Check if two paths are equal
 */
export const pathsEqual = (path1, path2) => {
  if (!path1 || !path2) {
    return path1 === path2;
  }

  if (path1.length !== path2.length) {
    return false;
  }

  for (let i = 0; i < path1.length; i++) {
    if (path1[i] !== path2[i]) {
      return false;
    }
  }

  return true;
};

/**
 * Get the depth of a path (how many levels deep)
 */
export const getPathDepth = (path) => {
  return path ? path.length : 0;
};

/**
 * Create a child path by appending an ID
 */
export const createChildPath = (parentPath, childId) => {
  return [...(parentPath || []), childId];
};

/**
 * Navigate to an element in a tree using a path
 * Returns the element at the path, or null if not found
 */
export const getElementAtPath = (elements, path) => {
  if (!path || path.length === 0) {
    return null;
  }

  const [firstId, ...restPath] = path;
  const element = elements.find(el => el.id === firstId);

  if (!element) {
    return null;
  }

  if (restPath.length === 0) {
    return element;
  }

  if (!element.children) {
    return null;
  }

  return getElementAtPath(element.children, restPath);
};

/**
 * Get the children array at a given path
 * If path is empty, returns root elements
 */
export const getChildrenAtPath = (elements, path) => {
  if (!path || path.length === 0) {
    return elements;
  }

  const element = getElementAtPath(elements, path);
  return element?.children || [];
};

/**
 * Update children at a specific path
 */
export const updateChildrenAtPath = (elements, path, newChildren) => {
  if (!path || path.length === 0) {
    return newChildren;
  }

  const [firstId, ...restPath] = path;

  return elements.map(element => {
    if (element.id === firstId) {
      if (restPath.length === 0) {
        return { ...element, children: newChildren };
      } else {
        return {
          ...element,
          children: updateChildrenAtPath(element.children || [], restPath, newChildren)
        };
      }
    }

    return element;
  });
};

/**
 * Insert an element at a specific path and index
 */
export const insertAtPath = (elements, path, element, index) => {
  const children = getChildrenAtPath(elements, path);
  const newChildren = [...children];
  newChildren.splice(index, 0, element);
  return updateChildrenAtPath(elements, path, newChildren);
};

/**
 * Remove an element at a specific path and index
 */
export const removeAtPath = (elements, path, index) => {
  const children = getChildrenAtPath(elements, path);
  const newChildren = [...children];
  newChildren.splice(index, 1);
  return updateChildrenAtPath(elements, path, newChildren);
};

/**
 * Move an element from one path to another
 */
export const moveElementBetweenPaths = (elements, sourcePath, sourceIndex, destPath, destIndex) => {
  // Get the element to move
  const sourceChildren = getChildrenAtPath(elements, sourcePath);
  const elementToMove = sourceChildren[sourceIndex];

  if (!elementToMove) {
    return elements;
  }

  // Remove from source
  let newElements = removeAtPath(elements, sourcePath, sourceIndex);

  // Adjust destination index if moving within same parent and moving down
  let adjustedDestIndex = destIndex;
  if (pathsEqual(sourcePath, destPath) && sourceIndex < destIndex) {
    adjustedDestIndex--;
  }

  // Insert at destination
  newElements = insertAtPath(newElements, destPath, elementToMove, adjustedDestIndex);

  return newElements;
};

/**
 * Get all paths in a tree (DFS traversal)
 */
export const getAllPaths = (elements, currentPath = []) => {
  const paths = [];

  elements.forEach(element => {
    const elementPath = [...currentPath, element.id];
    paths.push(elementPath);

    if (element.children && element.children.length > 0) {
      paths.push(...getAllPaths(element.children, elementPath));
    }
  });

  return paths;
};

/**
 * Find the path to an element by its ID
 */
export const findPathById = (elements, targetId, currentPath = []) => {
  for (const element of elements) {
    const elementPath = [...currentPath, element.id];

    if (element.id === targetId) {
      return elementPath;
    }

    if (element.children) {
      const foundPath = findPathById(element.children, targetId, elementPath);
      if (foundPath) {
        return foundPath;
      }
    }
  }

  return null;
};

/**
 * Get sibling elements at a path
 */
export const getSiblingsAtPath = (elements, path) => {
  if (!path || path.length === 0) {
    return [];
  }

  const parentPath = getParentPath(path);
  return getChildrenAtPath(elements, parentPath);
};

/**
 * Get the index of an element in its parent's children array
 */
export const getIndexInParent = (elements, path) => {
  if (!path || path.length === 0) {
    return -1;
  }

  const siblings = getSiblingsAtPath(elements, path);
  const elementId = getLastId(path);

  return siblings.findIndex(el => el.id === elementId);
};
